import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { listEvents } from '@/lib/eventsApi'
import type { EventImpact, RefreshStatsResponse } from '@/types/event'

export const EVENTS_KEY = ['events'] as const

export interface UseEventsListQuery {
  from: string
  to: string
  impact?: EventImpact
  countries?: string[]
}

/**
 * Top-N Events für das Dashboard-Widget. Default-Window: heute + 7 Tage.
 * impact_min='high' fest gesetzt (Konsistenz mit Backend-Tick-Filter).
 * Cache 60 s — Events ändern sich höchstens 1× pro Tag (Tick um 22:30).
 */
export function useEventsUpcoming(daysAhead: number = 7) {
  const today = new Date()
  const from = today.toISOString().slice(0, 10)
  const toDate = new Date(today)
  toDate.setDate(today.getDate() + daysAhead)
  const to = toDate.toISOString().slice(0, 10)

  return useQuery({
    queryKey: [...EVENTS_KEY, 'upcoming', daysAhead],
    queryFn: () =>
      listEvents({
        from,
        to,
        impact_min: 'high',
      }),
    staleTime: 60_000,
  })
}

/**
 * Events in einem konfigurierbaren Zeitfenster mit Filter-Optionen.
 * Pendant zu `useEventsUpcoming` für die /events-Page. Cache 60 s.
 */
export function useEventsList(query: UseEventsListQuery) {
  return useQuery({
    queryKey: [...EVENTS_KEY, 'list', query],
    queryFn: () =>
      listEvents({
        from: query.from,
        to: query.to,
        impact_min: query.impact,
        countries: query.countries,
      }),
    staleTime: 60_000,
  })
}

/**
 * Manueller Trigger für POST /events/refresh. Invalidiert alle
 * Event-Queries bei Erfolg → Liste/Widget refetchen automatisch.
 */
export function useRefreshEvents() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await api.post<RefreshStatsResponse>('/events/refresh')
      return response.data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EVENTS_KEY }),
  })
}
